import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import type { JsonPatchOp } from '@/features/admin-consent-types/endpoints';
import type { ConsentWireStatus } from '@/features/patient-record/api/consents';
import { mapConsent } from '@/features/patient-record/mappers/consent';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  ConsentStatus,
  ConsentViewModel,
} from '@/features/patient-record/types';

const UI_TO_WIRE_STATUS: Record<ConsentStatus, ConsentWireStatus> = {
  PENDING: 'pending',
  SIGNED: 'signed',
  DECLINED: 'declined',
  WITHDRAWN: 'withdrawn',
};

/** Editable fields — matches the RFC 6902 surface the BE accepts. */
export interface ConsentInput {
  readonly consentType: string;
  readonly status: ConsentStatus;
  readonly signedAt?: string | null;
  readonly signedBy?: string | null;
  readonly notes?: string | null;
}

/** Fields the caller wants to change; anything else is left as-is on the server. */
export type ConsentEditableField = keyof ConsentInput;

export interface PatchConsentVariables {
  readonly id: string;
  readonly input: ConsentInput;
  readonly changed: readonly ConsentEditableField[];
}

interface MutationContext {
  readonly previous: ConsentViewModel[] | undefined;
}

function toPatchOps({ input, changed }: PatchConsentVariables): JsonPatchOp[] {
  const ops: JsonPatchOp[] = [];
  for (const field of changed) {
    switch (field) {
      case 'consentType':
        ops.push({ op: 'replace', path: '/consent_type', value: input.consentType });
        break;
      case 'status':
        // The BE rejects a merged withdraw via PATCH; the UI must call
        // `/withdraw` for that transition.
        ops.push({
          op: 'replace',
          path: '/status',
          value: UI_TO_WIRE_STATUS[input.status],
        });
        break;
      case 'signedAt':
        ops.push({ op: 'replace', path: '/signed_at', value: input.signedAt ?? null });
        break;
      case 'signedBy':
        ops.push({ op: 'replace', path: '/signed_by', value: input.signedBy ?? null });
        break;
      case 'notes':
        ops.push({ op: 'replace', path: '/notes', value: input.notes ?? null });
        break;
    }
  }
  return ops;
}

type AddResult = UseMutationResult<ConsentViewModel, Error, ConsentInput, MutationContext>;
type PatchResult = UseMutationResult<
  ConsentViewModel,
  Error,
  PatchConsentVariables,
  MutationContext
>;
type TransitionResult = UseMutationResult<ConsentViewModel, Error, string, MutationContext>;
type DeleteResult = UseMutationResult<undefined, Error, string, MutationContext>;

/**
 * Variables for the `setDocumentRef` mutation.
 * Pass `null` (or empty string) to unlink the current attachment; the
 * underlying attachment record itself is left in place.
 */
export interface SetDocumentRefVariables {
  readonly id: string;
  readonly documentRef: string | null;
}

type SetDocumentRefResult = UseMutationResult<
  ConsentViewModel,
  Error,
  SetDocumentRefVariables,
  MutationContext
>;

/**
 * Resource-level mutations for patient consents. Split by API surface so
 * the UI can bind each call site to the right guarded transition:
 * `withdraw`/`reinstate` for state changes, `patch` for edits, `remove` for
 * soft-delete. See PTV2-1180 for the BE contract this maps onto.
 */
export function useConsentMutations(guid: string): {
  readonly add: AddResult;
  readonly patch: PatchResult;
  readonly withdraw: TransitionResult;
  readonly reinstate: TransitionResult;
  readonly remove: DeleteResult;
  readonly setDocumentRef: SetDocumentRefResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.consents(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const add = useMutation<ConsentViewModel, Error, ConsentInput, MutationContext>({
    meta: { successMessage: 'success.toast.title.consent_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.addConsent(guid, {
        consent_type: input.consentType,
        status: UI_TO_WIRE_STATUS[input.status],
        signed_at: input.signedAt ?? null,
        signed_by: input.signedBy ?? null,
        notes: input.notes ?? null,
      });
      return mapConsent(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      const optimistic: ConsentViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        consentType: input.consentType,
        status: input.status,
        signedAt: input.signedAt ?? null,
        signedBy: input.signedBy ?? null,
        notes: input.notes ?? null,
        documentRef: null,
      };
      queryClient.setQueryData(queryKey, [...(ctx.previous ?? []), optimistic]);
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const patch = useMutation<ConsentViewModel, Error, PatchConsentVariables, MutationContext>({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: async (vars) => {
      const ops = toPatchOps(vars);
      if (ops.length === 0) {
        const existing = (queryClient.getQueryData<ConsentViewModel[]>(queryKey) ?? []).find(
          (c) => c.id === vars.id,
        );
        if (existing !== undefined) return existing;
      }
      const dto = await apiClient.patchConsent(guid, vars.id, ops);
      return mapConsent(dto);
    },
    onSettled: settle,
  });

  const withdraw = useMutation<ConsentViewModel, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: async (id) => mapConsent(await apiClient.withdrawConsent(guid, id)),
    onSettled: settle,
  });

  const reinstate = useMutation<ConsentViewModel, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: async (id) => mapConsent(await apiClient.reinstateConsent(guid, id)),
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.consent_deleted' },
    mutationFn: (id) => apiClient.deleteConsent(guid, id),
    onSettled: settle,
  });

  // Isolated mutation for the attachment linkage — the attach/detach UI
  // wants to be able to fire this without staging a full ConsentInput.
  // Optimistic so the attach affordance flips to "linked" the instant the
  // upload succeeds, without waiting for the invalidation round-trip.
  const setDocumentRef = useMutation<
    ConsentViewModel,
    Error,
    SetDocumentRefVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.consent_updated' },
    mutationFn: async ({ id, documentRef }) => {
      const dto = await apiClient.patchConsent(guid, id, [
        { op: 'replace', path: '/document_ref', value: documentRef },
      ]);
      return mapConsent(dto);
    },
    onMutate: async ({ id, documentRef }) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<ConsentViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((c) => (c.id === id ? { ...c, documentRef } : c)),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { add, patch, withdraw, reinstate, remove, setDocumentRef };
}
